import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgcCookieConsentService } from 'ngx-cookieconsent';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  private lastNavigationUrl: string = '';
  private languageUpdatePending: boolean = false;

  private readonly pageTitleMap: { [key: string]: string } = {
    // Homepage
    '': 'Homepage',
    homepage: 'Homepage',
    login: 'Homepage',
    profil: 'Homepage',
    favorites: 'Homepage',
    'my-alerts': 'Homepage',
    'legal-notice': 'Homepage',
    'privacy-policy': 'Homepage',
    // Courses
    'courses-list': 'Courses',
    'courses-home': 'Courses',
    courses: 'Courses',
    'safe-training': 'SAFE training',
    'detail-safe-training': 'SAFE training',
    safeTraining: 'SAFE training',
    'detail-video': 'Courses',
    // Webinar replay
    replaywebinars: 'Webinar replay',
    'replay-webinar-list': 'Webinar replay',
    replaywebinar: 'Webinar replay',
    'detail-video-webinar': 'Webinar replay',
    // Webinar live
    livewebinars: 'Webinar live',
    livewebinar: 'Webinar live',
    'livewebinar-live': 'Webinar live',
    'livewebinar-youtube': 'Webinar live',
    'livewebinar-vimeo': 'Webinar live',
    // Events
    events: 'Events',
    'detail-event': 'Events',
    'events-detail': 'Events',
    // Scientific articles
    'case-studies': 'Scientific articles',
    // FAQ
    faq: 'FAQ',
    // Contact
    'contact-us': 'Contact',
  };

  constructor(
    private router: Router,
    private translate: TranslateService,
    private ccService: NgcCookieConsentService,
  ) {
    this.initializeTracking();
  }

  private initializeTracking(): void {
    // Track route changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.lastNavigationUrl = event.urlAfterRedirects;
        // Cancel any pending language update since NavigationEnd will handle it
        this.languageUpdatePending = false;
        this.updateDataLayer(event.urlAfterRedirects);
        // Track cookie consent on every page after consent is given
        this.trackCookieConsentIfGiven();
      });

    // Track language changes
    // Update dataLayer when language changes, but use a small delay to let NavigationEnd
    // fire first if it's going to (to avoid duplicate gtm.virtual_pageview events)
    // Note: We don't track ChangeLanguage event here because onLangChange fires on initialization
    // ChangeLanguage events are tracked explicitly in components where user actively changes language
    // (tabs.page.ts and login.page.ts)
    this.translate.onLangChange.subscribe((event) => {
      // Track cookie consent on every page after consent is given
      this.trackCookieConsentIfGiven();

      // Schedule dataLayer update with a small delay
      // If NavigationEnd fires, it will cancel this update
      if (event.lang) {
        this.languageUpdatePending = true;
        setTimeout(() => {
          // Only update if NavigationEnd didn't fire (URL didn't change)
          if (
            this.languageUpdatePending &&
            this.router.url === this.lastNavigationUrl
          ) {
            this.updateDataLayer(this.router.url);
          }
          this.languageUpdatePending = false;
        }, 100);
      }
    });
  }

  private getPageTitle(url: string): string {
    // Remove query params and hash
    const path = url.split('?')[0].split('#')[0];
    // Remove leading slash
    const cleanPath = path.replace(/^\//, '');

    // Split path to get segments
    const segments = cleanPath
      .split('/')
      .filter((segment) => segment.length > 0);

    // Check for exact matches first
    if (segments.length > 0) {
      const firstSegment = segments[0];

      // Handle nested routes
      if (firstSegment === 'tab1' || firstSegment === 'tabs') {
        if (segments.length > 1) {
          const secondSegment = segments[1];
          if (this.pageTitleMap[secondSegment]) {
            return this.pageTitleMap[secondSegment];
          }
        }
        return 'Homepage';
      }

      // Check for direct matches
      if (this.pageTitleMap[firstSegment]) {
        return this.pageTitleMap[firstSegment];
      }

      // Check for patterns like 'courses/:title/:id' or 'livewebinar/:title/:id'
      if (
        firstSegment === 'courses' ||
        firstSegment === 'livewebinar' ||
        firstSegment === 'replaywebinar' ||
        firstSegment === 'safeTraining'
      ) {
        return this.pageTitleMap[firstSegment] || 'Homepage';
      }
    }

    return 'Homepage';
  }

  private getDataStream(): string {
    // Check screen width to determine if mobile or web
    if (typeof window !== 'undefined' && window.innerWidth) {
      return window.innerWidth < 768 ? 'Mobile' : 'Web';
    }
    return 'Web';
  }

  private getLanguageVersion(): string {
    const currentLang =
      this.translate.currentLang || this.translate.defaultLang || 'fr';
    // Map language codes to uppercase
    const langMap: { [key: string]: string } = {
      fr: 'FR',
      en: 'EN',
      ru: 'RU',
      pl: 'PL',
      es: 'ES',
    };
    return langMap[currentLang] || 'FR';
  }

  private getEnvironment(): string {
    const hostname = window.location.hostname;
    const isProduction =
      hostname === 'vivacy-academy.com' ||
      hostname === 'www.vivacy-academy.com' ||
      hostname.indexOf('vivacy-academy.com') !== -1;
    return isProduction ? 'Production' : 'Preprod';
  }

  /**
   * Update dataLayer with virtual pageview event for SPA navigation
   * This method is called on every route change and sends a gtm.virtual_pageview event
   * instead of updating static dataLayer variables, as recommended by Google Analytics for SPAs
   * @param url - Optional URL to use instead of current router URL
   */
  public updateDataLayer(url?: string): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    const currentUrl = url || this.router.url;
    const pageTitle = this.getPageTitle(currentUrl);
    const dataStream = this.getDataStream();
    const languageVersion = this.getLanguageVersion();
    const environment = this.getEnvironment();

    // Use gtm.virtual_pageview event for SPA page changes
    // This event is triggered on every page change and includes all required variables
    window.dataLayer.push({
      event: 'gtm.virtual_pageview',
      nav_environment: environment,
      nav_data_stream: dataStream,
      nav_language_version: languageVersion,
      nav_template: 'VIVACY ACADEMY',
      page_title: pageTitle,
      page_path: currentUrl,
      page_location: window.location.href.split('?')[0].split('#')[0],
    });
  }

  public trackEvent(eventName: string, eventData?: any): void {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...eventData,
      });
    }
  }

  /**
   * Track cookie consent event if consent was already given
   * Called on every page after consent is given
   */
  private trackCookieConsentIfGiven(): void {
    try {
      if (this.ccService.hasConsented()) {
        this.trackCookieConsent(true);
      } else if (
        this.ccService.hasAnswered() &&
        !this.ccService.hasConsented()
      ) {
        this.trackCookieConsent(false);
      }
    } catch (e) {
      // Cookie service might not be initialized yet, ignore
    }
  }

  private lastConsentState: boolean | null = null;

  /**
   * Track cookie consent event
   * Updates Google Consent Mode so that GA4 can set _ga when user accepts.
   */
  public trackCookieConsent(hasConsented: boolean): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    // Prevent duplicate calls for the same consent state
    if (this.lastConsentState === hasConsented) {
      return;
    }
    this.lastConsentState = hasConsented;

    const consentState = {
      analytics_storage: (hasConsented ? 'granted' : 'denied') as 'granted' | 'denied',
      ad_storage: (hasConsented ? 'granted' : 'denied') as 'granted' | 'denied',
      ad_user_data: (hasConsented ? 'granted' : 'denied') as 'granted' | 'denied',
      ad_personalization: (hasConsented ? 'granted' : 'denied') as 'granted' | 'denied',
    };

    // Send consent update immediately so GA can set _ga (use gtag API as per Google docs)
    const gtag = (window as any).gtag;
    if (typeof gtag === 'function') {
      gtag('consent', 'update', consentState);
    } else {
      window.dataLayer.push(['consent', 'update', consentState]);
    }

    if (!hasConsented) {
      // If user refused, just send the event and return
      const eventName = 'cookies_all_refused';
      window.dataLayer.push({ event: eventName });
      return;
    }

    // For consent granted, wait for GTM to be ready, then send pageview
    // The pageview will trigger GA to create the _ga cookie
    const sendPageViewAfterGtmReady = (attempt: number = 0) => {
      const maxAttempts = 10;
      
      // Check if GTM is loaded
      const gtmLoaded = window.dataLayer && 
                       window.dataLayer.some((item: any) => item.event === 'gtm.load');

      if (gtmLoaded || attempt >= maxAttempts) {
        // GTM is loaded (or max attempts reached), send events
        const eventName = 'cookies_all_accepted';
        window.dataLayer.push({ event: eventName });
        
        // Send pageview to trigger _ga cookie creation
        // Use a longer delay to ensure consent update is processed by GTM first
        setTimeout(() => {
          this.updateDataLayer();
        }, 800);
      } else {
        // GTM not ready yet, wait a bit more
        setTimeout(() => {
          sendPageViewAfterGtmReady(attempt + 1);
        }, 200);
      }
    };

    // Wait for GTM to load, then send pageview
    sendPageViewAfterGtmReady();
  }

  /**
   * Track custom CTA click events
   * @param eventName - Name of the CTA (e.g., 'cta_Login', 'cta_inscription')
   * @param eventLabel - Optional label for the event
   */
  public trackCustomEvent(eventName: string, eventLabel?: string): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    const eventData: any = {
      event: 'gtm.custom_event',
      event_name: eventName,
      event_action: 'Click',
    };

    if (eventLabel) {
      eventData['event_label'] = eventLabel;
    }

    window.dataLayer.push(eventData);
  }

  /**
   * Track language change event
   * @param languageCode - Language code (fr, en, es, ru, pl)
   */
  public trackLanguageChange(languageCode: string): void {
    // Map language codes to uppercase
    const langMap: { [key: string]: string } = {
      fr: 'FR',
      en: 'EN',
      ru: 'RU',
      pl: 'PL',
      es: 'ES',
    };

    const languageLabel =
      langMap[languageCode.toLowerCase()] || languageCode.toUpperCase();
    this.trackCustomEvent('ChangeLanguage', languageLabel);
  }

  /**
   * Track authentication validation event (login or registration)
   * @param eventName - 'ConnexionValidation' for login or 'InscriptionValidation' for registration
   */
  public trackAuthValidation(
    eventName: 'ConnexionValidation' | 'InscriptionValidation',
  ): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: eventName,
      event_action: 'View',
    });
  }

  /**
   * Track video click event
   * @param video - Video object with titleCourse and other properties
   * @param isCourse - true if it's a course, false if it's a webinar
   * @param videoType - Optional video type (e.g., 'Stylage', 'Desirial')
   */
  public trackVideoClick(
    video: any,
    isCourse: boolean,
    videoType?: string,
  ): void {
    if (typeof window === 'undefined' || !window.dataLayer || !video) {
      return;
    }

    // Determine category based on video type
    // Categories: Courses | Webinar | Stylage | Desirial
    // Priority: Stylage/Desirial > Courses/Webinar
    let category: string;
    const actualVideoType = videoType || video.videoType;

    // First check videoType property (case-insensitive)
    if (actualVideoType && typeof actualVideoType === 'string') {
      const lowerCaseVideoType = actualVideoType.toLowerCase();
      if (lowerCaseVideoType === 'stylage') {
        category = 'Stylage';
      } else if (lowerCaseVideoType === 'desirial') {
        category = 'Desirial';
      }
    }

    // If videoType doesn't help, check product array for Stylage or Desirial
    if (!category && video.product && Array.isArray(video.product)) {
      for (const product of video.product) {
        const productName = (product.name || product || '')
          .toString()
          .toLowerCase();
        if (productName.includes('stylage')) {
          category = 'Stylage';
          break;
        } else if (productName.includes('desirial')) {
          category = 'Desirial';
          break;
        }
      }
    }

    // If still no category determined, use isCourse to determine Courses or Webinar
    if (!category) {
      category = isCourse ? 'Courses' : 'Webinar';
    }

    // Get video title in English (use titleCourse as default)
    const videoTitle = video.titleCourse || video.title || 'Unknown Video';

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'ClicVideo',
      event_action: 'Click',
      event_label: category,
      specialisation: videoTitle,
    });
  }

  /**
   * Track video play event (when user clicks play button)
   * @param video - Video object with titleCourse and other properties, OR category string directly
   * @param isCourse - true if it's a course, false if it's a webinar (ignored if video is a string category)
   * @param videoType - Optional video type (e.g., 'Stylage', 'Desirial')
   */
  public trackVideoPlay(
    video: any,
    isCourse?: boolean,
    videoType?: string,
  ): void {
    if (typeof window === 'undefined') {
      console.warn('GoogleAnalyticsService: window is undefined');
      return;
    }

    if (!window.dataLayer) {
      console.warn('GoogleAnalyticsService: dataLayer is not initialized');
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];
    }

    // If video is a string, it's actually a category (for direct category tracking)
    let category: string;
    let videoTitle: string;

    if (typeof video === 'string') {
      // Direct category passed (e.g., 'Stylage', 'Desirial', 'Courses', 'Webinar')
      category = video;
      videoTitle = videoType || 'Unknown Video'; // videoType is actually title in this case
    } else {
      if (!video) {
        console.warn(
          'GoogleAnalyticsService: video object is missing for trackVideoPlay',
        );
        return;
      }

      // Determine category based on video type
      // Categories: Courses | Webinar | Stylage | Desirial
      // Priority: Stylage/Desirial > Courses/Webinar

      // First check if videoType indicates Stylage or Desirial (these take priority)
      const actualVideoType = videoType || video.videoType;
      if (actualVideoType === 'Stylage') {
        category = 'Stylage';
      } else if (actualVideoType === 'Desirial') {
        category = 'Desirial';
      } else {
        // If not Stylage or Desirial, use isCourse to determine Courses or Webinar
        category = isCourse ? 'Courses' : 'Webinar';
      }

      // Get video title in English (use titleCourse as default)
      videoTitle = video.titleCourse || video.title || 'Unknown Video';
    }

    const eventData = {
      event: 'gtm.custom_event',
      event_name: 'PlayVideo',
      event_action: 'Click',
      event_label: category,
      specialisation: videoTitle,
    };

    window.dataLayer.push(eventData);
  }

  /**
   * Track video play event with direct category (for use in VideoPlayerModal)
   * @param category - Video category directly (Courses | Webinar | Stylage | Desirial)
   * @param videoTitle - Video title in English
   */
  public trackVideoPlayWithCategory(
    category: string,
    videoTitle: string,
  ): void {
    if (typeof window === 'undefined') {
      console.warn('GoogleAnalyticsService: window is undefined');
      return;
    }

    if (!window.dataLayer) {
      console.warn('GoogleAnalyticsService: dataLayer is not initialized');
      window.dataLayer = window.dataLayer || [];
    }

    if (!category || !videoTitle) {
      console.warn(
        'GoogleAnalyticsService: Missing required parameters for trackVideoPlayWithCategory',
        {
          category,
          videoTitle,
        },
      );
      return;
    }

    const eventData = {
      event: 'gtm.custom_event',
      event_name: 'PlayVideo',
      event_action: 'Click',
      event_label: category,
      specialisation: videoTitle,
    };

    window.dataLayer.push(eventData);
  }

  /**
   * Format seconds to "M:SS" or "MM:SS" format
   * @param seconds - Time in seconds
   * @returns Formatted time string like "0:43" or "12:05"
   */
  private formatVideoTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Track video pause event (when user clicks pause button)
   * @param category - Video category (Courses | Webinar | Stylage | Desirial)
   * @param videoTitle - Video title in English
   * @param videoTimeSeconds - Current playback time in seconds
   */
  public trackVideoPause(
    category: string,
    videoTitle: string,
    videoTimeSeconds: number,
  ): void {
    if (typeof window === 'undefined') {
      console.warn('GoogleAnalyticsService: window is undefined');
      return;
    }

    if (!window.dataLayer) {
      console.warn('GoogleAnalyticsService: dataLayer is not initialized');
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];
    }

    if (!category || !videoTitle) {
      console.warn(
        'GoogleAnalyticsService: Missing required parameters for trackVideoPause',
        {
          category,
          videoTitle,
          videoTimeSeconds,
        },
      );
      return;
    }

    // Format time as "M:SS" or "MM:SS"
    const formattedTime = this.formatVideoTime(videoTimeSeconds);

    const eventData = {
      event: 'gtm.custom_event',
      event_name: 'PauseVideo',
      event_action: 'Click',
      event_label: category,
      specialisation: videoTitle,
      video_time: formattedTime,
    };

    window.dataLayer.push(eventData);
  }

  /**
   * Track live webinar join event (when user clicks to join a live)
   * @param liveTitle - Live webinar title in English
   */
  public trackLiveJoin(liveTitle: string): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'Live',
      event_action: 'Click',
      event_label: liveTitle,
    });
  }

  /**
   * Track country/language click event in live webinar (when user changes language)
   * @param country - Country name (ENGLISH | FRENCH | SPANISH | RUSSIAN)
   */
  public trackCountryClick(country: string): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'CountryClick',
      event_action: 'Click',
      event_country: country,
    });
  }

  /**
   * Track form click event in live webinar (when user clicks on form link)
   * @param formLabel - Form label (English Form | Return Form)
   */
  public trackFormClick(formLabel: string): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'ClicForm',
      event_action: 'Click',
      event_label: formLabel,
    });
  }

  /**
   * Track search event (when user performs a search)
   * @param searchTerm - Search keyword entered by the user
   */
  public trackSearch(searchTerm: string): void {
    if (
      typeof window === 'undefined' ||
      !window.dataLayer ||
      !searchTerm ||
      searchTerm.trim() === ''
    ) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'Search',
      event_action: 'View',
      event_label: searchTerm.trim(),
    });
  }

  /**
   * Extract English value from filter list by name
   * @param filterList - List of filter objects (e.g., bodyZones, specialities)
   * @param parameterName - Name of the parameter to find
   * @returns English value or empty string
   */
  private getEnglishValue(filterList: any[], parameterName: string): string {
    if (!filterList || !parameterName) {
      return '';
    }
    const found = filterList.find((item) => item.name === parameterName);
    return found && found.valueEn ? found.valueEn : '';
  }

  /**
   * Extract English values from multiple parameters
   * @param filterList - List of filter objects
   * @param parameters - Array of parameter names
   * @returns Comma-separated English values or empty string
   */
  private getEnglishValues(filterList: any[], parameters: string[]): string {
    if (!parameters || parameters.length === 0) {
      return '';
    }
    const values = parameters
      .map((param) => this.getEnglishValue(filterList, param))
      .filter((val) => val !== '');
    return values.join(', ');
  }

  /**
   * Track filter click event (when user applies filters)
   * @param filterData - Object containing filter parameters and their lists
   */
  public trackFilters(filterData: {
    zone?: string;
    speciality?: string;
    expert?: string;
    video_type?: string;
    needle_type?: string;
    product?: string;
    language?: string;
  }): void {
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'Filters',
      event_action: 'Click',
      event_label: 'Filters',
      zone: filterData.zone || '',
      speciality: filterData.speciality || '',
      expert: filterData.expert || '',
      video_type: filterData.video_type || '',
      needle_type: filterData.needle_type || '',
      product: filterData.product || '',
      language: filterData.language || '',
    });
  }

  /**
   * Track scientific article view event (when user clicks to open an article)
   * @param articleTitle - Title of the article
   * @param articleType - Type of article (Desirial | Stylage)
   */
  public trackViewArticle(articleTitle: string, articleType: string): void {
    if (typeof window === 'undefined') {
      console.warn('Google Analytics: window is undefined');
      return;
    }

    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
      window.dataLayer = [];
      console.warn(
        'Google Analytics: dataLayer was not initialized, creating it now',
      );
    }

    // Ensure articleType is one of the allowed values
    const validType =
      articleType === 'Desirial' || articleType === 'Stylage'
        ? articleType
        : '';

    const eventData = {
      event: 'gtm.custom_event',
      event_name: 'ViewArticles',
      event_action: 'Click',
      event_label: articleTitle || '',
      specialisation: validType,
    };

    console.log('Google Analytics: Tracking ViewArticles event:', eventData);

    window.dataLayer.push(eventData);
  }

  /**
   * Track contact form submission confirmation (when user sees the success message)
   */
  public trackContactForm(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Initialize dataLayer if it doesn't exist
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      event: 'gtm.custom_event',
      event_name: 'ContactForm',
      event_action: 'View',
    });
  }
}
